import { Capacitor } from '@capacitor/core'

import { Logger } from '../logger'
import { PushNotificationListener } from './listener'
import { PushNotificationPermissions } from './permissions'
import { PushNotificationCallback, PushNotificationEvents, PushNotificationListeners } from './types'

const logger = new Logger('PushNotificationService')

class PushNotificationService {
  constructor(
    private pushNotificationsPermissions = new PushNotificationPermissions(),
    private pushNotificationsListeners = new PushNotificationListener()
  ) {}

  public async initialize(listeners: PushNotificationListeners) {
    if (Capacitor.getPlatform() === 'web') {
      logger.info('Skipping initialization on web platform')
      return
    }

    logger.info('Initializing push notifications...')

    try {
      this.pushNotificationsListeners.initialize({
        onRegistration: listeners.onRegistration,
        onRegistrationError: listeners.onRegistrationError,
        onReceived: listeners.onReceived,
        onActionPerformed: listeners.onActionPerformed,
      })
      logger.info('Listeners initialized')

      const permission = await this.pushNotificationsPermissions.request()

      if (!permission.granted) {
        logger.warn('Push notification permission not granted, aborting initialization')
        return
      }

      await this.pushNotificationsPermissions.register()
      logger.success('Push notifications initialized successfully')
    } catch (error) {
      logger.error('Error initializing push notifications', error)
    }
  }

  public async unregister() {
    if (Capacitor.getPlatform() === 'web') {
      logger.info('Skipping unregistration on web platform')
      return
    }

    logger.info('Unregistering push notification listeners...')

    try {
      await this.pushNotificationsListeners.removeAllListeners()
      logger.success('Push notification listeners removed')
    } catch (error) {
      logger.error('Error unregistering listeners', error)
    }
  }

  public on(event: PushNotificationEvents, callback: PushNotificationCallback) {
    this.pushNotificationsListeners.on(event, callback)
  }

  public off(event: PushNotificationEvents, callback: PushNotificationCallback) {
    this.pushNotificationsListeners.off(event, callback)
  }
}

export const pushNotificationService = new PushNotificationService()
